import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import _ from "lodash";
import { createBrowserHistory } from "history";

import moment from "moment-timezone";

import {
  Header,
  Table,
  Tab,
  Popup,
  Icon,
  Dropdown,
  Input,
  Pagination,
  Loader,
} from "semantic-ui-react";
import CreateTimerButtonContainer from "../../containers/createTimerButtonContainer";
import Clipboard from "react-clipboard.js";
import UpgradePlanButton from "../billing/upgradePlanButton";

import timerHelper from "../../helpers/timers/timerHelper";
import TimerPreview from "../shared/timerPreview";

import css from "../../assets/styles/timerList/index.module.scss";
import tableCss from "../../assets/styles/shared/tables.module.scss";

import gridCss from "../../assets/styles/shared/grids.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  DeleteIconComponent,
  EditIconComponent,
  LeftArrowIconComponent,
  RightArrowIconComponent,
} from "../iconComponents";
import { ClipLoader } from "react-spinners";

const NewTimerList = (props) => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [archived, setArchived] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [sortOptions] = useState([
    { key: 111001, text: "Creation date", value: "created_at", sort: "desc" },
    { key: 111002, text: "End date", value: "ends_at", sort: "desc" },
    { key: 111003, text: "Total views", value: "total_views", sort: "desc" },
    {
      key: 111004,
      text: "Views (this billing cycle)",
      value: "paid_views",
      sort: "desc",
    },
  ]);

  const [currentSort, setCurrentSort] = useState({
    key: 111001,
    text: "Creation date",
    value: "created_at",
    sort: "desc",
  });

  useEffect(() => {
    props.fetch(page, archived, searchText, currentSort, setLoading);
  }, [page, archived, searchText, currentSort]);

  const format24HoursDate = (date) => {
    return moment(date).format("MMM DD, YYYY HH:mm");
  };

  const handleEdit = (timerId) => {
    return () => {
      navigate("/timer/" + timerId);
    };
  };

  const handleArchive = (timerId, archived) => {
    return (e) => {
      e.stopPropagation();
      e.preventDefault();
      props.archive(timerId, !archived, page);
    };
  };

  const renderEditIcon = (archived, timer) => {
    if (archived) {
      return null;
    }

    return (
      <Popup
        inverted
        trigger={
          <span style={{ cursor: "pointer" }} onClick={handleEdit(timer.id)}>
            <EditIconComponent />
          </span>
        }
        content={"Edit"}
        position={"top center"}
      />
    );
  };

  const renderArchiveIcon = (archived, id) => {
    const info = archived ? "Unarchive" : "Archive";

    return (
      <Popup
        inverted
        trigger={
          <span
            style={{ cursor: "pointer" }}
            name="trash"
            onClick={handleArchive(id, archived)}
          >
            <DeleteIconComponent />
          </span>
        }
        content={info}
        position={"top center"}
      />
    );
  };

  const renderCopyLink = (url) => {
    if (url) {
      const copyButton = (
        <Clipboard component="span" data-clipboard-text={url}>
          <Icon name="linkify" className={tableCss.linkIcon} />
        </Clipboard>
      );

      return (
        <Popup
          inverted
          trigger={copyButton}
          content="Copied!"
          position="top center"
          hideOnScroll
        />
      );
    } else {
      return (
        <Popup
          inverted
          content="Upgrade your plan to use this timer"
          position="top center"
          hideOnScroll
          trigger={<Icon name="linkify" disabled />}
        />
      );
    }
  };

  const handleTimerPaginationChange = (e, { activePage }) =>
    setPage(activePage);

  const renderZeroState = (status) => {
    if (status === "active") {
      return (
        <div className={tableCss.zero}>
          You do not currently have any active timers. Create one with the
          button in the top right!
        </div>
      );
    } else {
      return (
        <div className={tableCss.zero}>
          You do not currently have any archived timers.
        </div>
      );
    }
  };

  const renderEndDate = (timer) => {
    if (timer.timer_type === "standard") {
      // const endsAt = moment(timer.ends_at + " GMT+0000").tz(timer.time_zone);
      // // console.log("Ends At: ",endsAt," Timer ends at: ",timer.ends_at,timer.time_zone);
      // return formatDate(endsAt);
      const endsAt = moment.tz(timer.ends_at, timer.time_zone);
      return endsAt.format("MMM DD, YYYY HH:mm");
    } else if (timer.timer_type === "perpetual") {
      const durationObject = timerHelper.getDurationObject(timer.duration);
      const { days, hours, minutes, seconds } = durationObject;
      return `Perpetual (${days}d ${hours}h ${minutes}m)`;
    } else if (timer.timer_type === "dynamic") {
      return "(Dynamic Timer)";
    } else if (timer.timer_type === "recurring") {
      return "(Recurring Timer)";
    } else {
      return null;
    }
  };


  const renderTimerCurry = (showUid) => {
    return (timer) => {
      const timerUrl = timer.url;
      const createdAt = moment(timer.created_at).tz(timer.time_zone);
      const endsAt = moment(timer.ends_at).tz(timer.time_zone);
      const isExpired =
        endsAt.isBefore(moment()) &&
        !timer.archived &&
        timer.timer_type === "standard";
      const features = timerHelper.getTimerFeatures(timer);
      const missingFeatures = timerHelper.getMissingFeatures(
        features,
        props.user
      );

      return (
        <Table.Row
          className={isExpired ? css.expired : ""}
          key={"timer_" + timer.id}
        >
          <Table.Cell className={tableCss.name}>
            {timer.name}
            <Popup
              basic
              position="right center"
              trigger={<Icon name="eye" />}
              content={
                <TimerPreview
                  type="Timer"
                  timer={timer}
                  genUrl={props.user.auth.gen_url}
                />
              }
            />

            {missingFeatures.length && props.user.subscription ? (
              <Popup
                basic
                position="right center"
                trigger={<Icon color="red" name="exclamation circle" />}
                content={"This timer uses features not included in your plan."}
              />
            ) : null}
            {/* {renderDisableAtWarning(timer)} */}
          </Table.Cell>
          {showUid ? <Table.Cell>{timer.uid}</Table.Cell> : null}
          <Table.Cell>{format24HoursDate(createdAt)}</Table.Cell>
          <Table.Cell>{renderEndDate(timer)}</Table.Cell>
          <Table.Cell>{timer.total_views}</Table.Cell>
          <Table.Cell>{timer.paid_views}</Table.Cell>
          <Table.Cell className={tableCss.actionDiv}>
            {renderEditIcon(timer.archived, timer)}
            {renderArchiveIcon(timer.archived, timer.id)}
            {renderCopyLink(timerUrl)}
          </Table.Cell>
        </Table.Row>
      );
    };
  };

  const renderTimers = (timers, showUid) => {
    return _.map(timers, renderTimerCurry(showUid));
  };

  const renderTableContents = (status) => {
    // setArchived(status === "active" ? false : true);

    const timers = props.newTimer?.data?.data ?? [];

    const showUid = _.some(timers, (timer) => timer.uid);

    return (
      <>
        <div className={tableCss.filterDiv}>
          <div>
            <Input
              className={gridCss.gridSearchText}
              icon
              iconPosition="left"
              placeholder="Search"
            >
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <Icon name="search" />
            </Input>
          </div>
          <div>
            <span style={{ paddingRight: "16px" }}>Sort by</span>
            <Dropdown
              placeholder="Sort"
              defaultValue={currentSort.value}
              onChange={(e, data) => setCurrentSort(data.value)}
              selection
              options={sortOptions}
            />
          </div>
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ClipLoader color={"#123abc"} loading={loading} size={30} />
          </div>
        ) : (
          <>
            <Table basic className={tableCss.tableBorder}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  {showUid ? <Table.HeaderCell>UID</Table.HeaderCell> : null}
                  <Table.HeaderCell>Creation date</Table.HeaderCell>
                  <Table.HeaderCell>End date</Table.HeaderCell>
                  <Table.HeaderCell>Total views</Table.HeaderCell>
                  <Table.HeaderCell>
                    Views
                    <span className={tableCss.secondary}>
                      (this billing cycle)
                    </span>
                  </Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                <Table.Row className={tableCss.tableRowBackground}>
                  <Table.HeaderCell
                    className={tableCss.tableCol}
                    colSpan={showUid ? "7" : "6"}
                  >
                    {props.newTimer?.data?.meta?.total_entries} total timers
                  </Table.HeaderCell>
                </Table.Row>
                {renderTimers(timers, showUid)}
              </Table.Body>
            </Table>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span className={tableCss.tableCol}>
                  {timers.length} out of{" "}
                  {props.newTimer?.data?.meta?.total_entries}
                </span>
              </div>
              <div>
                <Pagination
                  defaultActivePage={props.newTimer?.data?.meta?.current_page}
                  onPageChange={handleTimerPaginationChange}
                  totalPages={props.newTimer?.data?.meta?.total_pages}
                  lastItem={null}
                  firstItem={null}
                  ellipsisItem={{
                    content: <Icon name="ellipsis horizontal" />,
                    icon: true,
                  }}
                  prevItem={{
                    content: (
                      <span>
                        <LeftArrowIconComponent />
                      </span>
                    ),
                    icon: true,
                  }}
                  nextItem={{
                    content: (
                      <span>
                        <RightArrowIconComponent />
                      </span>
                    ),
                    icon: true,
                  }}
                />
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  const renderTable = (status) => {
    return () => {
      return (
        <Tab.Pane as="div" attached="false">
          {renderTableContents(status)}
        </Tab.Pane>
      );
    };
  };

  const renderTabs = () => {
    const tables = [
      { menuItem: "Active Timers", render: renderTable("active") },
      { menuItem: "Archived Timers", render: renderTable("archived") },
    ];

    return (
      <Tab 
        menu={{ secondary: true, pointing: true }} 
        panes={tables} 
        onTabChange={(e, { activeIndex }) => {
          setPage(1);
          setArchived(activeIndex === 0 ? false : true);
        }}
      />
    );
  };

  const renderCreateTimerButton = () => {
    return <CreateTimerButtonContainer>Create New</CreateTimerButtonContainer>;
  };

  return (
    <div id="timer-list" className={tableCss.tableContainer}>
      <Header as="h1">Timers</Header>
      <div className={tableCss.tableActions}>
        {renderCreateTimerButton()}
        {props.user.subscription ? null : (
          <UpgradePlanButton>Upgrade Plan</UpgradePlanButton>
        )}
      </div>
      {renderTabs()}
    </div>
  );
};

export default NewTimerList;
